import React, { useCallback, useEffect, useReducer, useRef } from "react"

import { getInstantWin, qualifyingEvent, createEntry } from "services/instant-win"

import { ScrollToStepButton } from 'components/instant-win/ScrollToStepButton'
import { SingleClickButton } from 'components/instant-win/SingleClickButton'
import InstantWinModal from "components/instant-win/InstantWinModal"

import analytics from "global/AnalyticsData"

import { DialogMessage } from "components/instant-win/DialogMessage" 

import { getRandomInt } from "util/maths"
import { userTypeEnum } from 'services/user'


const instantWinReducer = (state, action) => {

    // always include current action.type in state. allows useEffect to support loading data.
    // anti-pattern? greatest pattern?
    state.currentAction = action.type
    switch (action.type) {
        case "LOADED_DATA":
            return {
                ...state,
                isReady: true,
                isError: false,
                instantWinConfig: action.instantWinConfig,
                data: action.data,
                currentAction:"LOADED_DATA"
            }
        // this break is not configured w/ instant win
        case "NO_INSTANT_WIN":
            return {
                ...state,
                isReady: true,                    
            }    
        case "QUALIFIED_EVENT":
            return {
                ...state,
                isModalOpen: true,
                context: action.context,
                data: action.data,
                token: action.token,
                wheelConfig: action.wheelConfig,
                button: action.button,
                prizePool: action.prizePool,
                hasSecondTry: action.hasSecondTry,
                secondTryStepId: action.secondTryStepId,
            }
        case "UNQUALIFIED_EVENT":
            return {
                ...state,
                isModalOpen: false,
                token: null,
            }

        case "UNQUALIFIED_EVENT_WITH_MESSAGE":
            return {
                ...state,
                isModalOpen: true,
                errorMessage: action.message,
                token: null,
            }
        // allows us to send wheel info down to modal. may not need separate WINNER/LOSER
        case "ENTRY_RESULT_WINNER":
            return {
                ...state,
                isWinner: action.isWinner,
                hasSecondTry: action.hasSecondTry,
                wheelConfig: action.wheelConfig,
                handleAfterSpin: action.handleAfterSpin,
                prize: action.prize,
            }

        case "ENTRY_RESULT_LOSER":
            return {
                ...state,
                isWinner: action.isWinner,
                hasLostOnce: action.hasLostOnce,
                hasLostTwice: action.hasLostTwice,
                wheelConfig: action.wheelConfig,
                handleAfterSpin: action.handleAfterSpin,
                prize: null,
            }

        // SECOND_ENTRY_RESULT_WINNER & SECOND_ENTRY_RESULT_LOSER
        // are very similar to ENTRY_RESULT_WINNER & ENTRY_RESULT_LOSER
        // duplicating felt like it would be more clear
        case "SECOND_ENTRY_RESULT_WINNER":
            return {
                ...state,
                isWinner: action.isWinner,
                hasSecondTry: action.hasSecondTry,
                wheelConfig: action.wheelConfig,
                handleAfterSpin: action.handleAfterSpin,
                prize: action.prize,
            }

        case "SECOND_ENTRY_RESULT_LOSER":
            return {
                ...state,
                isWinner: action.isWinner,
                hasLostOnce: action.hasLostOnce,
                hasLostTwice: action.hasLostTwice,
                wheelConfig: action.wheelConfig,
                handleAfterSpin: action.handleAfterSpin,
                prize: null,
            }
    
            // triggered after wheel is done spinning
        // we show updated copy 
        case "DISPLAY_RESULT":
            return {
                ...state,
                wheelConfig: action.wheelConfig,
                button: action.button
            }
        case "QUALIFIED_SECOND_CHANCE":
            return {
                ...state,
                wheelConfig: action.wheelConfig,
                button: action.button,
                isModalOpen: true,
            }
        case "CLOSE_MODAL":
            return {
                ...state,
                isModalOpen: false,
            }

        default:
            console.warn(`unhandled action.type: ${action.type}`)
            // throw new Error()
    }
}

/**
    this handles the mess of looking up whether an event is applicable for us
    it transforms a step-specific payload into a common format

    @param eventContext comes from the source event that sent this event

    @returns
    {
        payload                 // reshaped eventContext based on eventName
        prizePoolId             // determined
    }

    note: not inside the controller. pass params for what you need.
    

*/
const isValidEvent = (eventName, eventContext, instantWinConfig={events:[]}, permalink, isSecondChance) => {

    if (!instantWinConfig) {
        return
    }

    if (isNaN(instantWinConfig.id)) {
        return
    }

    
    const eventsWithPermalinks = instantWinConfig.events.filter((instantWinEvent) => {
        return instantWinEvent.permalink && (instantWinEvent.permalink !== "")
    })

    const eventsWithoutPermalinks = instantWinConfig.events.filter((instantWinEvent) => {
        return !instantWinEvent.permalink || (instantWinEvent.permalink === "")
    })

    // first look for a permalink-specific match. These are more specific so should be checked first.
    let configuredEvent = eventsWithPermalinks.find((instantWinEvent) => {
        return ((instantWinEvent.eventName === eventName) && 
                (instantWinEvent.isSecondTry === isSecondChance) && 
                (instantWinEvent.permalink === permalink) 
                )
    })

    // couldn't find one? now try looking for one that matches that has no permalink
    if (!configuredEvent) {
        configuredEvent = eventsWithoutPermalinks.find((instantWinEvent) => {
            return ((instantWinEvent.eventName === eventName) && 
                    (instantWinEvent.isSecondTry === isSecondChance) 
                    )
        })    
    }

    if (configuredEvent) {
        // different events have different payloads, depending on thei step. we need to map their 
        // payload format to a common one for instant win

        if (configuredEvent.eventName === "leadgen_submit") {
            let payload = eventContext.form
            payload.eventName = eventName
            payload.stepId = eventContext.stepId
            payload.instantWinId =  Number(instantWinConfig.id)
            payload.leadGenSubmissionToken = payload.id
            delete payload.token

            return {
                payload: payload,
                prizePoolId: configuredEvent.prizePoolId
            }

        }

        if (configuredEvent.eventName === "receipt-submission-status-approved") {
            let payload = eventContext.receiptStatusResult
            payload.eventName = eventName
            payload.stepId = eventContext.stepId
            payload.instantWinId = Number(instantWinConfig.id)
            payload.receiptSubmissionToken = payload.token
            delete payload.token

            return {
                payload: payload,
                prizePoolId: configuredEvent.prizePoolId
            }

        }

        // share gives us basically no info. It's really only useful as a second try.
        if (configuredEvent.eventName === "share_click") {
            return {
                payload: {},
                prizePoolId: configuredEvent.prizePoolId ? configuredEvent.prizePoolId : null
            }
        }
        
    } 

    return null

}

const InstantWinController = ({ render, brk, permalink, instantWinId, userType }) => {

    // this click handler is defined first so we can use it in <Button onClick /> in the useReducer definition
    // rename it to handleStartSpin
    const handleModalButtonClick = async (context) => {

        console.log(`handleModalButtonClick`)
    
        // let's see if we won!
        const response = await createEntry(stateRef.current.instantWinId, stateRef.current.token, stateRef.current.email)
        const data = await response.json()
        console.log(`response`, data)

        if (data.winner === true) {

            const prize = stateRef.current.prizePool.prizes.find((prize, i) => {
                return (prize.id === data.prizeId)
            }) 

            analytics.stepEvent("step_interaction_instant_win_won", context, {
                result_id: instantWinId,
                result: "won",
            })

            dispatch({
                type: "ENTRY_RESULT_WINNER", 
                isWinner: true,
                wheelConfig: Object.assign({}, stateRef.current.wheelConfig, { shouldSpin : true, prizeId:data.prizeId, hideForfeit: true }),
                handleAfterSpin: handleAfterSpin,
                prize: prize
            })    
        } else {
            
            const loserPrize = stateRef.current.data.find((element) => {
                return (element.id === 0)
            })

            analytics.stepEvent("step_interaction_instant_win_lost", context, {
                result_id: instantWinId,
                result: "lost",
            })

            dispatch({
                type: "ENTRY_RESULT_LOSER", 
                isWinner: false,
                hasLostOnce: true,
                wheelConfig: Object.assign({}, stateRef.current.wheelConfig, { shouldSpin : true, prizeId: loserPrize.id, hideForfeit: true }),
                handleAfterSpin: handleAfterSpin,
            })    
    
        }    
    
    }

    // this almost duplicates handleModalButtonClick
    const handleSecondChanceButtonClick = async (context={}) => {

        console.log("handleSecondChanceButtonClick")

        // let's see if we won!
        const response = await createEntry(stateRef.current.instantWinId, stateRef.current.token, stateRef.current.email)
        const data = await response.json()
        console.log(`response`, data)
    
        // more hard-coded logic here
        if (data.isWinner === true) {

            const prize = stateRef.current.prizePool.prizes.find((prize, i) => {
                return (prize.id === data.prizeId)
            }) 

            analytics.stepEvent("step_interaction_instant_win_second_chance_won", context, {
                result_id: instantWinId,
                result: "won",
            })

            dispatch({
                type: "SECOND_ENTRY_RESULT_WINNER", 
                isWinner: true,
                hasSecondTry: false,
                hasLostTwice: false,
                wheelConfig: Object.assign({}, stateRef.current.wheelConfig, { shouldSpin : true, prizeId: data.prizeId, hideForfeit: true }),
                handleAfterSpin: handleAfterSpinSecondTry,
                prize: prize
            })    
        } else {
            
            const loserPrize = stateRef.current.data.find((element) => {
                return (element.id == 0)
            })

            analytics.stepEvent("step_interaction_instant_win_second_chance_lost", context, {
                result_id: instantWinId,
                result: "lost",
            })

            dispatch({
                type: "SECOND_ENTRY_RESULT_LOSER", 
                isWinner: false,
                hasLostOnce: true,
                hasLostTwice: true,
                wheelConfig: Object.assign({}, stateRef.current.wheelConfig, { shouldSpin : true, prizeId: loserPrize.id, hideForfeit: true }),
                handleAfterSpin: handleAfterSpinSecondTry,
                prize: loserPrize,
            })    
    
        }    

    }
    /*
        the data sent to configure wheel items takes the form
        const data = [
            { id: 0, option: 'Sorry' },
            { id: 1, option: '$500' },
            { id: 2, option: '$100' },
            { id: 3, option: '$10' },
            { id: 4, option: 'Sorry' },
            { id: 5, option: '$500' },
            { id: 6, option: '$100' },
            { id: 7, option: '$10' },
            { id: 8, option: 'Sorry' }
        ]

    */
    const [state, dispatch] = useReducer(instantWinReducer, {
        currentAction: "INIT",
        brk: brk,
        context: {instantWinId : instantWinId, permalink: permalink},
        // breakId: brk.id,
        permalink: permalink,
        instantWinId: instantWinId,
        isReady: false,
        isError: false,
        isModalOpen: false,
        instantWinConfig: null,
        prizePool: null,
        hasSecondTry: false,
        secondTryStepId: null,
        hasLostOnce: false,
        hasLostTwice: false,
        secondChanceUsed: false,
        prizeNumber: 0,
        button: null,
        wheelConfig: { shouldSpin : false, data:[], hideForfeit: false },
        errorMessage: null,
    })

    // storing our state in a useRef() ?
    // was getting an odd behavior where event handlers were running and seeing old state
    // https://stackoverflow.com/questions/57847594/react-hooks-accessing-up-to-date-state-from-within-a-callback
    // so we throw state in a ref. feels gross.
    const stateRef = useRef();
    stateRef.current = state

   // console.log(`currentAction`, state.currentAction, state.context, state.instantWinConfig)

    useEffect(() => {
        const asyncWrapper = async () => {
            // only kick things off after we have a user token in place
            if ((instantWinId != null) && (userType && (userType != userTypeEnum.NONE))) {
                const result = await getInstantWin(instantWinId)
            
                // we can't determine prizes without a prize pool specified, which would happen when an event occurs
                dispatch({
                type: "LOADED_DATA",
                instantWinConfig: result,
                })
            } else {
                dispatch({
                    type: "NO_INSTANT_WIN",
                })
            }
        }

        // don't run our initialization code on builds, do it at runtime
        if (typeof window != "undefined") {
            asyncWrapper()
        }
    }, [instantWinId, userType])


    // incoming instant win events
    const handleEvent = async ({ eventName, context={} }) => {
        console.log(`instant win event received: ${eventName}`, context)
        if (stateRef.current.instantWinId === null) {
            return
        }

        const isSecondTry = ((stateRef.current.hasSecondTry == true) && 
                                (stateRef.current.hasLostOnce === true) && 
                                (stateRef.current.secondChanceUsed === false))

        const validEvent = isValidEvent(eventName, context, stateRef.current.instantWinConfig, stateRef.current.permalink, isSecondTry)

        console.log(`TEST`, validEvent)

        if (validEvent) {
            console.log("HERE WE GO")

            // overlay whatever is new on top of whatever we had
            // needed for events like video_share that give us no data
            const newContext = Object.assign({}, stateRef.current.context, {eventName: eventName}, validEvent.payload)
            
            // either our event mapping has a poolId or we fall back on whatever we had
            let prizePoolId = stateRef.current.prizePool ? stateRef.current.prizePool.id : null
            if (validEvent.prizePoolId) {
                prizePoolId = validEvent.prizePoolId
            } 

            // we didn't get a prize pool from the event - if we only have one defined, use it
            if (!prizePoolId) {
                try {
                    if (stateRef.current.instantWinConfig.prizePools && stateRef.current.instantWinConfig.prizePools.length == 1) {
                        prizePoolId = stateRef.current.instantWinConfig.prizePools[0].id
                    }    
                } catch (e) {
                    console.log(e)
                }
            }

            if (!prizePoolId) {
                console.warn("Invalid/missing prize pool")
                return
            }

            newContext['instantWinPrizePoolId'] = prizePoolId

            // API CALL to see if this was a valid qualifying event
            const response = await qualifyingEvent(newContext)          // validEvent.payload
            const qualifyingEventResult = await response.json()

            try {
                if (qualifyingEventResult.eventStatus === "SUCCESSFUL") {
                    console.log(`response`, qualifyingEventResult)

                    // lookup the wheel data asspciated with the receipt step reward they chose
                    const prizePool = stateRef.current.instantWinConfig.prizePools.find((element) => {
                        return (element.id == prizePoolId)
                    })
    
                    if (!prizePool) {
                        console.warn("Can't find prizepool for ", payload.rewardId)
                        return
                    }
                    // format wheel data
                    const wheelData = prizePool.prizes.map((item, i) => {
                        return {id: item.id, option: item.title}
                    })
            
                    wheelData.unshift({id: 0, option: 'Sorry!'})    // unshift to make this prize 0
    
                    let wheelConfig = stateRef.current.wheelConfig
                    wheelConfig.title = prizePool.modalLaunchTitle
                    wheelConfig.bodyCopy = prizePool.modalLaunchBodyCopy
                    wheelConfig.footerCopy = prizePool.sharedFooterCopy
                    wheelConfig.buttonCopy = prizePool.modalLaunchButtonCopy
    
                    console.log("isSecondTry", isSecondTry)


                    if (isSecondTry) {
                        let button = <SingleClickButton label="Try again!" onClick={handleSecondChanceButtonClick} className="instantWinButton" />
                        dispatch({
                            type: "QUALIFIED_SECOND_CHANCE", // OPEN_MODAL
                            token: qualifyingEventResult.token,
                            context: newContext,
                            wheelConfig: wheelConfig,
                            button: button,                            
                        })
        
                    } else {
                        let button = <SingleClickButton label={prizePool.modalLaunchButtonCopy} onClick={handleModalButtonClick} className="instantWinButton" />

                        dispatch({
                            type: "QUALIFIED_EVENT", // OPEN_MODAL
                            token: qualifyingEventResult.token,
                            data: wheelData,
                            prizePool: prizePool,
                            context: newContext,
                            wheelConfig: wheelConfig,
                            button: button,
                            hasSecondTry: prizePool.hasSecondTry,
                            secondTryStepId: prizePool.secondTryStepId,
                        })
    
                    }

                } else {
                    console.log("Invalid qualifying event", qualifyingEventResult)

                    if (qualifyingEventResult.message) {
                        dispatch({
                            type: "UNQUALIFIED_EVENT_WITH_MESSAGE", 
                            token: qualifyingEventResult.token,
                            context: newContext,
                            message: qualifyingEventResult.message
                        })
    
                    }
                    // not sure what we should keep track of here
                    dispatch({
                        type: "UNQUALIFIED_EVENT", // NO SPIN MODAL
                        token: qualifyingEventResult.token,
                        context: newContext,
                    })
    
                }    
            } catch (e) {
                // generally catches our own data issues
                console.warn(e)
            }

            return
        }

    }

    const handleCloseModal = () => {
        analytics.trackEvent("step_interaction_instant_win_modal_close", {
            result_id: instantWinId,
            result: "close",
        })
        
        dispatch({
            type: "CLOSE_MODAL",
        })
    }


    // Wheel component will call this after spinning is complete
    // this allows us to update copy based on win/lose
    const handleAfterSpin = async () => {
        console.log("AFTER SPIN" )
        // update winner / loser copy
        
        let wheelConfig = stateRef.current.wheelConfig
        wheelConfig.shouldSpin = false
        let button = null

        if (stateRef.current.isWinner) {
            wheelConfig.title = stateRef.current.prize.winnerModalTitle
            wheelConfig.bodyCopy = stateRef.current.prize.winnerModalBodyCopy
            // TODO: button copy/url from winnerModalButtonCopy/winnerModalButtonUrl

            button = <SingleClickButton label="Close" onClick={handleCloseModal} className="instantWinButton" key={getRandomInt(1,999999)} />
        } else {
            // when a pool has a second chance lose copy is second try copy. a concept that is confusing.
            wheelConfig.title = stateRef.current.prizePool.modalLoseTitle
            wheelConfig.bodyCopy = stateRef.current.prizePool.modalLoseBodyCopy

            if (stateRef.current.prizePool.hasSecondTry) {
                button = <ScrollToStepButton 
                            label={stateRef.current.prizePool.modalLoseButtonCopy} 
                            stepId={stateRef.current.prizePool.secondTryStepId} 
                            onClickCallback={handleCloseModal} 
                            className="instantWinButton" />
            }

        }

        dispatch({
            type: "DISPLAY_RESULT",
            wheelConfig: wheelConfig,
            button: button
        })

    }

    // Wheel component will call this after spinning is complete
    // this allows us to update copy based on win/lose
    const handleAfterSpinSecondTry = async () => {
        console.log("AFTER SPIN 2ND TRY" )
        // update winner / loser copy
        
        let wheelConfig = stateRef.current.wheelConfig
        wheelConfig.shouldSpin = false
        let button = null

        if (stateRef.current.isWinner) {
            wheelConfig.title = stateRef.current.prize.winnerModalTitle
            wheelConfig.bodyCopy = stateRef.current.prize.winnerModalBodyCopy

            button = <SingleClickButton label="Close" onClick={handleCloseModal} className="instantWinButton" key={getRandomInt(1,999999)} />   // key to force a remount
        } else {
            wheelConfig.title = stateRef.current.prizePool.modalLose2Title
            wheelConfig.bodyCopy = stateRef.current.prizePool.modalLose2BodyCopy

            button = <SingleClickButton label="Close" onClick={handleCloseModal} className="instantWinButton" key={getRandomInt(1,999999)} />  // key to force a remount
        }

        dispatch({
            type: "DISPLAY_RESULT",
            wheelConfig: wheelConfig,
            button: button
        })

    }



    const memoizedHandleEvent = useCallback(handleEvent, []);

    let instantWinModal = null

    if (typeof window != "undefined") {
        if (stateRef.current.errorMessage) {

            instantWinModal = <DialogMessage title={"Error"} message={stateRef.current.errorMessage } />

        } else {
            instantWinModal = <InstantWinModal 
                wheelConfig={stateRef.current.wheelConfig} 
                data={stateRef.current.data} 
                isModalOpen={stateRef.current.isModalOpen} 
                handleCloseModal={handleCloseModal}                                  
                button={stateRef.current.button} 
                handleAfterSpin={stateRef.current.handleAfterSpin} 
            />
        }

    }

    // we only want the eventHandler to be made available once we're ready. 
    if (stateRef.current.instantWinConfig) {
        return render(stateRef.current.isReady, instantWinModal, memoizedHandleEvent )
    } else{
        return render(stateRef.current.isReady, instantWinModal, null )
    }



}

export { InstantWinController }
